/* Deload periods (state.deloads = [{ id, from, to, style, note?, by }], keyed
 * by id): while one runs, targets get lighter, and its sessions never count as
 * a regression, a progression step or a program week. Twin of
 * src/fitlogic/deload.py, pinned by vectors/deload.json. */

const DATE_RE = /^[0-9]{4}-[0-9]{2}-[0-9]{2}$/;
const isNum = v => typeof v === 'number' && Number.isFinite(v);
const isObj = v => !!v && typeof v === 'object' && !Array.isArray(v);

/* volume: half the sets (rounded up) at ~90%; light: every set at ~60%. */
export const DELOAD_STYLES = {
  volume: { label: 'Fewer sets', setsFactor: 0.5, weightFactor: 0.9 },
  light: { label: 'Lighter weights', setsFactor: 1, weightFactor: 0.6 },
};

function validDate(k) {
  if (typeof k !== 'string' || !DATE_RE.test(k)) return false;
  const [y, m, d] = k.split('-').map(Number);
  const t = new Date(Date.UTC(y, m - 1, d));
  return t.getUTCFullYear() === y && t.getUTCMonth() === m - 1 && t.getUTCDate() === d;
}

function entries(state) {
  const list = isObj(state) && Array.isArray(state.deloads) ? state.deloads : [];
  return list.filter(e => isObj(e) && validDate(e.from) && validDate(e.to) && e.from <= e.to);
}

/**
 * The deload covering `date` (the latest-starting one), or null.
 * @returns {{id, from, to, style: 'volume'|'light', note}|null}
 */
export function deloadOn(state, date) {
  if (!validDate(date)) return null;
  let best = null;
  for (const e of entries(state)) {
    if (e.from <= date && date <= e.to && (!best || e.from > best.from)) best = e;
  }
  if (!best) return null;
  return {
    id: typeof best.id === 'string' ? best.id : null,
    from: best.from,
    to: best.to,
    style: DELOAD_STYLES[best.style] ? best.style : 'volume',
    note: typeof best.note === 'string' && best.note.trim() ? best.note.trim() : null,
  };
}

export function isDeloadDate(state, date) {
  return deloadOn(state, date) !== null;
}

/**
 * state.logs without the dates inside a deload (keepDate stays), for trends,
 * progression and "last time". The same object when there are no deloads.
 */
export function trainingLogs(state, keepDate = null) {
  const logs = isObj(state) && isObj(state.logs) ? state.logs : {};
  const list = entries(state);
  if (!list.length) return logs;
  const out = {};
  for (const k of Object.keys(logs)) {
    if (k !== keepDate && list.some(e => e.from <= k && k <= e.to)) continue;
    out[k] = logs[k];
  }
  return out;
}

/**
 * Target sets for a deload: the style's share of the sets (at least 1), each
 * weight times its factor rounded to `step`, no AMRAP, pct dropped.
 */
export function deloadSets(sets, style, step = 2.5) {
  const st = DELOAD_STYLES[style] || DELOAD_STYLES.volume;
  const list = Array.isArray(sets) ? sets.filter(isObj) : [];
  if (!list.length) return [];
  const s = isNum(step) && step > 0 ? step : 2.5;
  const n = Math.max(1, Math.ceil(list.length * st.setsFactor));
  return list.slice(0, n).map(x => ({
    ...x,
    weight: isNum(x.weight) ? Math.round(Math.round((x.weight * st.weightFactor) / s) * s * 100) / 100 : x.weight,
    amrap: false,
    pct: null,
  }));
}
